import { CohortEnum } from '@eigtech/auth0-types'
import { PublicationTargets } from '@eigtech/documents-types'
import { Screenshot } from '@eigtech/meetings-types'
import { Corn, getCornKernels } from '@eigtech/shared-corn'
import { useCohortContext } from '@eigtech/ui-shared-api'
import {
  useCanPublishClaimInfoToSymbility,
  useCanPublishClaimInfoToXA,
} from '@eigtech/ui-shared-claims-lib'
import {
  DataGrid,
  FilterFnOption,
  RowSelectionState,
  Table,
  createColumnHelper,
} from '@eigtech/ui-shared-data-grid'
import {
  DateTime,
  STANDARD_DATE_TIME_FORMAT,
  formatStringAsDate,
  secondsToMilliseconds,
  useDatesContext,
} from '@eigtech/ui-shared-dates'
import {
  Button,
  ButtonGroup,
  Center,
  Checkbox,
  ComposedAlert,
  ComposedCard,
  Editable,
  EditablePreview,
  EditableTextarea,
  HStack,
  Heading,
  Image,
  Skeleton,
  Text,
  Tooltip,
  chakra,
  pluralize,
  useEditableContext,
} from '@eigtech/ui-shared-dave'
import { LinkButton } from '@eigtech/ui-shared-router'
import { toast } from '@eigtech/ui/components/sonner'
import { CreatableSelect } from 'chakra-react-select'
import { startCase, uniq } from 'lodash-es'
import { useEffect, useMemo, useRef, useState } from 'react'
import { MdCheck } from 'react-icons/md'
import { useAddScreenshotLabel } from '../../api/addScreenshotLabel'
import { useGetMeetingUploads } from '../../api/getMeeting'
import { useGetScreenshotLabels } from '../../api/getScreenshotLabels'
import { useGetScreenshotUrl } from '../../api/getScreenshotUrl'
import { usePublishScreenshots } from '../../api/publishScreenshots'
import { useUpdateScreenshotDescription } from '../../api/updateScreenshotDescription'
import { MeetingInfo, MeetingUploadRoute } from './types'

export type ScreenshotTableProps = {
  meetingUploadRoute?: MeetingUploadRoute
} & MeetingInfo

export function ScreenshotTable({
  entityId,
  meetingId,
  meetingUploadRoute,
  startTime,
}: ScreenshotTableProps) {
  const { resourceType } = getCornKernels(entityId)

  const canPublishToXA = useCanPublishClaimInfoToXA({ claimCorn: entityId })
  const canPublishToSymbility = useCanPublishClaimInfoToSymbility({ claimCorn: entityId })

  const {
    data: uploads,
    isPending: isPendingUploads,
    isFetching: isFetchingUploads,
    isError: isUploadsError,
    refetch: refetchUploads,
  } = useGetMeetingUploads({
    entityId,
    meetingId,
    startTime,
  })

  const {
    mutateAsync: publishScreenshots,
    isPending: isPublishing,
    isError: isPublishError,
  } = usePublishScreenshots()

  const { cohort } = useCohortContext()

  async function handlePublishScreenshots(target: Exclude<PublicationTargets, 'XactAnalysis'>) {
    if (!table.current) return

    const cohorts: CohortEnum[] = uniq([cohort, 'csr', 'super'])

    await publishScreenshots({
      entityId,
      startTime,
      dateAdded: table.current
        .getSelectedRowModel()
        .flatRows.map(({ original: { dateAdded } }) => dateAdded),
      publishTo: target === 'documents' ? [] : [target],
      userCohorts: cohorts,
    })

    toast.success('Success!', {
      description: `Selected screenshots have been published. They will show up in the Media tab${target !== 'documents' ? ` and ${target} ` : ' '}in a few minutes.`,
      duration: secondsToMilliseconds(30),
    })

    table.current.resetRowSelection()
  }

  const table = useRef<Table<Screenshot>>(null)

  const [createdFilterFn, setCreatedFilterFn] = useState<FilterFnOption<Screenshot>>('dateEquals')

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'bulkActions',
        maxSize: 20,
        header: ({ table }) => (
          <Center w="full">
            <Checkbox
              isChecked={table.getIsAllRowsSelected()}
              isIndeterminate={table.getIsSomePageRowsSelected()}
              onChange={table.getToggleAllPageRowsSelectedHandler()}
            />
          </Center>
        ),
        cell: ({ row }) =>
          row.original.publishedOn ? (
            <Tooltip
              label={`Published on ${formatStringAsDate(
                row.original.publishedOn,
                STANDARD_DATE_TIME_FORMAT
              )}`}
            >
              <MdCheck />
            </Tooltip>
          ) : (
            <Center>
              <Checkbox isChecked={row.getIsSelected()} onChange={row.getToggleSelectedHandler()} />
            </Center>
          ),
        enableHiding: false,
        enableSorting: false,
        meta: {
          hidePinButton: true,
          enableReordering: false,
        },
      }),
      columnHelper.accessor('label', {
        id: 'label',
        header: 'Label',
        cell: ({ row }) => (
          <EditScreenshotLabel
            entityId={entityId}
            meetingId={meetingId}
            screenshot={row.original}
            startTime={startTime}
          />
        ),
      }),
      columnHelper.accessor('description', {
        id: 'file',
        header: 'Description',
        cell: ({ row }) => (
          <ScreenshotDescription
            entityId={entityId}
            meetingId={meetingId}
            screenshot={row.original}
            startTime={startTime}
          />
        ),
      }),
      columnHelper.accessor('filename', {
        id: 'fileName',
        header: 'File Name',
      }),

      columnHelper.accessor('dateAdded', {
        id: 'dateAdded',
        header: 'Date Uploaded',
        filterFn: createdFilterFn,
        meta: {
          availableFilterFns: [
            { label: 'Date Equals', value: 'dateEquals' },
            { label: 'Date Greater Than', value: 'dateGreaterThan' },
            { label: 'Date Lesser Than', value: 'dateLesserThan' },
          ],
          onFilterFnChange: setCreatedFilterFn,
        },
        cell: ({ getValue }) => <DateCell property="dateAdded">{getValue()}</DateCell>,
      }),
      columnHelper.accessor((row) => row.metadata?.photoTime ?? '', {
        id: 'dateTaken',
        header: 'Date Taken',
        cell: ({ getValue }) =>
          getValue() ? <DateCell property="dateTaken">{getValue()}</DateCell> : null,
      }),
      columnHelper.display({
        id: 'download',
        header: '',
        maxSize: 80,
        enableHiding: false,
        enableSorting: false,
        enableColumnFilter: false,
        cell: (cell) => <Photo {...cell.row.original} entityId={entityId} startTime={startTime} />,
      }),
    ],
    [createdFilterFn, entityId, startTime, meetingId]
  )

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const numberOfSelectedRows = Object.keys(rowSelection).length

  return (
    <ComposedCard
      heading={
        <HStack>
          <Heading as="h3" flex="1" size="md">
            Inspection Screenshots
          </Heading>

          {!!meetingUploadRoute && (
            <LinkButton
              params={{} as never}
              search={{} as never}
              to={meetingUploadRoute({ entityId, meetingId, startTime })}
            >
              Upload Screenshots
            </LinkButton>
          )}
        </HStack>
      }
    >
      {isPublishError && (
        <ComposedAlert
          alert={{
            title: 'Could not publish screenshots.',
            description:
              'Something went wrong trying to publish selected screenshots. Please try again.',
          }}
          status="error"
        />
      )}
      <DataGrid
        ref={table}
        columns={columns}
        data={uploads ?? []}
        isError={isUploadsError}
        isFetching={isFetchingUploads}
        isPending={isPendingUploads}
        meta={{
          bulkActions: numberOfSelectedRows > 0 && (
            <ButtonGroup size="sm" spacing="1">
              {canPublishToXA && (
                <Button
                  key="Xactimate"
                  isLoading={isPublishing}
                  variant="outline"
                  onClick={() => handlePublishScreenshots('Xactimate')}
                >
                  Publish {numberOfSelectedRows} {pluralize('Screenshot', numberOfSelectedRows)} to
                  Xactimate
                </Button>
              )}
              {canPublishToSymbility && (
                <Button
                  key="Symbility"
                  isLoading={isPublishing}
                  variant="outline"
                  onClick={() => handlePublishScreenshots('Symbility')}
                >
                  Publish {numberOfSelectedRows} {pluralize('Screenshot', numberOfSelectedRows)} to
                  Symbility
                </Button>
              )}
              <Button
                key="Entity"
                isLoading={isPublishing}
                variant="outline"
                onClick={() => handlePublishScreenshots('documents')}
              >
                Publish {numberOfSelectedRows} {pluralize('Screenshot', numberOfSelectedRows)} to{' '}
                {startCase(resourceType)}
              </Button>
            </ButtonGroup>
          ),
        }}
        refetch={refetchUploads}
        state={{ rowSelection }}
        onRowSelectionChange={setRowSelection}
      />
    </ComposedCard>
  )
}

const columnHelper = createColumnHelper<Screenshot>()

function Photo({
  dateAdded,
  entityId,
  startTime,
}: Screenshot & { entityId: Corn; startTime: string }) {
  const { data: url, refetch } = useGetScreenshotUrl({ dateAdded, entityId, startTime })

  useEffect(() => {
    refetch()
  }, [refetch])

  return (
    <chakra.a href={url} target={`${entityId}${startTime}${dateAdded}`}>
      <Image fallback={<Skeleton h="20" w="20" />} minW="20" src={url} />
    </chakra.a>
  )
}

function DateCell(props: {
  children: DateTime
  format?: string
  secondaryFormat?: string
  property: string
}) {
  const { PreferredDateTimeComponent } = useDatesContext()

  return <PreferredDateTimeComponent {...props} />
}

function EditScreenshotLabel({
  screenshot,
  entityId,
  meetingId,
  startTime,
}: {
  screenshot: Screenshot
  entityId: Corn
  meetingId: string
  startTime: string
}) {
  return (
    <Tooltip
      hasArrow={false}
      isDisabled={!screenshot.label}
      label="Click to Edit"
      placement="bottom-start"
    >
      <Editable
        defaultValue={screenshot.label || '(none)'}
        minW="250"
        value={screenshot.label || '(none)'}
      >
        <EditablePreview cursor={'pointer'} />
        <EditableCreateSelectable
          dateAdded={screenshot.dateAdded}
          description={screenshot.description ?? screenshot.notes ?? ''}
          entityId={entityId}
          label={screenshot.label}
          meetingId={meetingId}
          startTime={startTime}
        />
      </Editable>
    </Tooltip>
  )
}

function EditableCreateSelectable({
  entityId,
  meetingId,
  startTime,
  dateAdded,
  label,
  description,
}: {
  entityId: Corn
  meetingId: string
  startTime: string
  dateAdded: string
  label?: string
  description: string
}) {
  const { isEditing, onSubmit: closeEditable } = useEditableContext()
  const { data: screenshotLabels, isLoading: isLoadingScreenshotLabels } = useGetScreenshotLabels({
    entityId,
    meetingId,
    startTime,
  })

  const { mutateAsync: addScreenshotLabel, isPending: isAddScreenshotLabelPending } =
    useAddScreenshotLabel()
  const { mutateAsync: updateScreenshotDescription, isPending: isPublishingNote } =
    useUpdateScreenshotDescription()

  return (
    isEditing && (
      <CreatableSelect
        isClearable={false}
        isDisabled={isLoadingScreenshotLabels || isAddScreenshotLabelPending || isPublishingNote}
        isLoading={isLoadingScreenshotLabels || isAddScreenshotLabelPending || isPublishingNote}
        isRequired
        options={screenshotLabels?.labels}
        value={
          label
            ? {
                label: label,
                value: label.toUpperCase(),
              }
            : undefined
        }
        onBlur={() => closeEditable()}
        onChange={async (newValue) => {
          await updateScreenshotDescription({
            description: description,
            label: newValue?.value ?? '',
            dateAdded: dateAdded,
            entityId,
            startTime,
            meetingId,
          })
          closeEditable()
        }}
        onCreateOption={async (newOption) => {
          addScreenshotLabel({ entityId, meetingId, startTime, label: newOption })
          await updateScreenshotDescription({
            description: description,
            label: newOption?.toUpperCase() ?? '',
            dateAdded: dateAdded,
            entityId,
            startTime,
            meetingId,
          })
          closeEditable()
        }}
      />
    )
  )
}

function ScreenshotDescription(props: {
  screenshot: Screenshot
  entityId: Corn
  meetingId: string
  startTime: string
}) {
  const [editModeEnabled, setEditModeEnabled] = useState(false)

  return (
    <Tooltip
      hasArrow={false}
      isDisabled={!props.screenshot.description}
      label="Click to Edit"
      placement="bottom-start"
      shouldWrapChildren
    >
      {editModeEnabled ? (
        <EditScreenshotDescription {...props} />
      ) : (
        <Text cursor="pointer" onClick={() => setEditModeEnabled(true)}>
          {props.screenshot.description}
        </Text>
      )}
    </Tooltip>
  )
}

function EditScreenshotDescription({
  screenshot,
  entityId,
  meetingId,
  startTime,
}: {
  screenshot: Screenshot
  entityId: Corn
  meetingId: string
  startTime: string
}) {
  const { mutate: updateScreenshotNotes, isPending: isPublishingNote } =
    useUpdateScreenshotDescription()

  return (
    <Editable
      defaultValue={screenshot.description || '(none)'}
      isDisabled={isPublishingNote}
      minW="250"
      startWithEditView
      onSubmit={(next) => {
        updateScreenshotNotes({
          description: next,
          label: screenshot.label ?? 'UNLABELED',
          dateAdded: screenshot.dateAdded,
          entityId,
          startTime,
          meetingId,
        })
      }}
    >
      <EditablePreview cursor="pointer" />
      <EditableTextarea maxLength={95} />
    </Editable>
  )
}
